import '@widesk-ui/components/styles/Value.scss';

import React                         from 'react';
import { HTMLAttributeAnchorTarget } from 'react';
import Link                          from '@widesk-ui/components/Link';
import OneLineSkeleton               from '@widesk-ui/components/OneLineSkeleton';
import dayjs                         from 'dayjs';
import View                          from '@widesk-ui/components/View';
import Badge                         from '@widesk-ui/components/Badge';
import Button                        from '@widesk-ui/components/Button';
import { Modal }                     from '@widesk-ui/components/modal/Modal';
import formatPhoneNumber             from '@widesk-ui/tools/formatPhoneNumber';
import formatPrice                   from '@widesk-core/tools/formatPrice';
import Observer                      from '@widesk-ui/components/Observer';
import { Dayjs }                     from 'dayjs';
import getFlagFromCountryCode        from '@widesk-ui/tools/getFlagFromCountryCode';
import { AllCountries }              from '@widesk-ui/tools/countries';
import BoolTag                       from '@widesk-ui/components/BoolTag';
import copyToClipboard               from '@widesk-ui/tools/copyToClipboard';
import { CopyOutlined }              from '@ant-design/icons';
import useTheme                      from '@widesk-ui/hooks/useTheme';

export type ValueType =
	'boolean'
	| 'date'
	| 'dateRange'
	| 'dateTime'
	| 'timeRange'
	| 'time'
	| 'color'
	| 'phone'
	| 'email'
	| 'percent'
	| 'price'
	| 'html'
	| 'array'
	| 'gapArray'
	| 'country';

type Val = React.ReactNode | Dayjs;
type ValOrFunction = Val | (() => Val);

export type ValueProps<V extends ValOrFunction> = {
	enableCopy?: boolean;
	multiple?: boolean;
	style?: React.CSSProperties;
	type?: ValueType;
	linkTarget?: HTMLAttributeAnchorTarget;
	linkSecondary?: boolean;
	loading?: boolean;
	empty?: boolean;
	ellipsis?: boolean;
	tag?: boolean;
} & ({
	value: V;
	link?: string;
} | {
	value: V[];
	link?: (value: V) => string;
});

export default function Value<V extends ValOrFunction>(props: ValueProps<V>) {
	const { link, empty, loading, ellipsis, tag } = props;

	const style: React.CSSProperties = {
		...(() => {
			if (ellipsis) {
				return {
					textOverflow: 'ellipsis',
					overflow: 'hidden',
					whiteSpace: 'nowrap',
				};
			}

			return {};
		})(),
		...(props.enableCopy ? { cursor: 'pointer' } : undefined),

		...props.style,
	};

	return (
		<Observer render={() => {
			const value = (typeof props.value === 'function' ? props.value() : props.value) as React.ReactNode;
			let type = props.type;

			if (empty) return <EmptyValue />;

			if (loading) return <OneLineSkeleton />;

			if (typeof value === 'undefined' || value === '') return <EmptyValue />;

			if (Array.isArray(value)) {
				if (!value.length) return <EmptyValue />;

				const values = (value as V[]).map((v, index) => (
					<React.Fragment key={index}>
						{type === 'dateRange' && index === 0 && 'Du '}
						<Value
							{...props as any}
							link={(link && typeof link !== 'string') ? link(v) : undefined}
							multiple={false}
							value={v}
							style={{ display: 'inline' }}
							type={type === 'timeRange' ? 'time' : undefined}
						/>

						{type === 'dateRange' && index === 0 && ' au '}
						{type !== 'dateRange'
							&& type !== 'gapArray'
							&& type !== 'timeRange'
							&& index !== value.length - 1
							&& ', '}
						{type === 'timeRange' && index !== value.length - 1 && ' - '}
					</React.Fragment>
				));

				return type === 'gapArray'
					? <View gap="xxs" row wrap>{values}</View>
					: values;
			}

			// Quand le type n'est pas définis, on essaye de le deviner.
			if (typeof type === 'undefined') {
				const uv = value as unknown;
				if (typeof uv === 'boolean') type = 'boolean';
				else if (typeof uv === 'string' && uv.startsWith('#') && uv.length === 7) type = 'color';
				else if (dayjs.isDayjs(uv)) type = 'date';
			}

			let displayedValue = value;
			let copiedValue: string | undefined = undefined;

			switch (type) {
				case 'boolean':
					if (tag) {
						copiedValue = value ? 'Oui' : 'Non';
						displayedValue = <BoolTag boolValue={value as boolean} style={{ marginBottom: 2 }} />;
					} else {
						displayedValue = value ? 'Oui' : 'Non';
					}
					break;
				case 'date':
				case 'dateRange':
					displayedValue = dayjs(value as never).format('L');
					break;
				case 'dateTime':
					displayedValue = dayjs(value as never).format('L LT');
					break;
				case 'time':
					displayedValue = dayjs(value as never).format('HH[h]mm');
					break;
				case 'color':
					copiedValue = value as string;
					displayedValue = <Badge color={value as string} text={value} />;
					break;
				case 'phone':
					copiedValue = value as string;
					displayedValue = <a href={`tel:${value}`}>{formatPhoneNumber(value as string)}</a>;
					break;
				case 'email':
					copiedValue = value as string;
					displayedValue = <a href={`mailto:${value}`}>{value}</a>;
					break;
				case 'percent':
					displayedValue = value + '%';
					break;
				case 'price':
					displayedValue = formatPrice(value as number || 0);
					break;
				case 'html': {
					copiedValue = undefined;
					if (value && typeof value === 'string') {
						const breakLinesValue = value
							.replace(/<\/p>/g, '</p>\n')
							.replace(/<br>/g, '\n');
						const copiedDocument = new DOMParser().parseFromString(breakLinesValue, 'text/html');
						copiedValue = copiedDocument.body.textContent || '';
					}

					displayedValue = <HtmlValue value={value as string} seeMore enableCopy />;
					break;
				}
				case 'country':
					if (typeof value === 'string') {
						displayedValue = (
							<View centerV flex gap="xxs" row>
								{getFlagFromCountryCode(value as CountryCode)}
								<span>{AllCountries[value as CountryCode]}</span>
							</View>
						);
					}
					break;
			}

			return (
				<span
					className={`widesk-value ${type} ${props.enableCopy ? 'copy-enabled' : ''}`}
					style={style}
					title={(ellipsis && typeof displayedValue === 'string') ? displayedValue : undefined}
					onClick={props.enableCopy
						? () => copyToClipboard(copiedValue || displayedValue as string)
						: undefined
					}
				>
					{link ?
						<Link
							to={link as string}
							target={props.linkTarget}
							className={props.linkSecondary ? 'secondary' : undefined}
						>
							{displayedValue}
						</Link> :
						displayedValue
					}
				</span>
			);
		}} />
	);
}

export const EmptyValue = () => {
	const theme = useTheme();
	return <View inline bold color={theme.colorTextDisabled}>N/A</View>;
};

const HtmlValue = (props: { value?: string, seeMore?: boolean, enableCopy?: boolean }) => {
	return (
		<View>
			<div className="type-html" dangerouslySetInnerHTML={{ __html: props.value?.toString() || '' }}></div>
			{props.seeMore && (props.value?.length || 0) > 250 && (
				<View right>
					<Button
						type="link"
						onClick={e => {
							e.stopPropagation();
							return Modal.open({
								title: 'Texte complet',
								children: <HtmlValue value={props.value} />,
								footer: props.enableCopy ? (
									<Button icon={<CopyOutlined />} onClick={() => {
										if (!props.value) {
											return;
										}

										const copiedValue = new DOMParser().parseFromString(props.value, 'text/html').body.textContent || '';
										copyToClipboard(copiedValue);
									}}>
										Copier
									</Button>
								) : null,
								maskClosable: true,
							});
						}}>
						Voir la suite
					</Button>
				</View>
			)}
		</View>
	);
};
